import { useState, useEffect, useMemo } from "react";
import { api } from "../lib/api";
import type {
  EngagementType,
  EngagementOutcome,
  EngagementActivity,
} from "@hsh/shared";
import {
  ENGAGEMENT_TYPE_LABELS,
  ENGAGEMENT_TYPE_ICONS,
  ENGAGEMENT_OUTCOME_LABELS,
  CATEGORY_LABELS,
} from "@hsh/shared";

// Local type definitions (matching server types)
type ReportingPeriod =
  | "7days"
  | "14days"
  | "30days"
  | "90days"
  | "thisMonth"
  | "lastMonth"
  | "thisQuarter"
  | "thisYear"
  | "custom";

const REPORTING_PERIOD_LABELS: Record<ReportingPeriod, string> = {
  "7days": "Last 7 Days",
  "14days": "Last 14 Days",
  "30days": "Last 30 Days",
  "90days": "Last 90 Days",
  thisMonth: "This Month",
  lastMonth: "Last Month",
  thisQuarter: "This Quarter",
  thisYear: "This Year",
  custom: "Custom Range",
};

interface ActivityDataPoint {
  date: string;
  total: number;
  byType: Record<EngagementType, number>;
  byOutcome: Record<EngagementOutcome, number>;
}

interface UserEngagementStats {
  userId: string;
  userName: string;
  totalActivities: number;
  activitiesByType: Record<EngagementType, number>;
  activitiesByOutcome: Record<EngagementOutcome, number>;
  businessesContacted: number;
  meetingsBooked: number;
  referralsMade: number;
  avgActivitiesPerDay: number;
  mostActiveDay?: string;
  streak?: number;
}

interface EngagementActivityWithPlace extends EngagementActivity {
  placeId: string;
  placeName: string;
  placeCategory: string;
}

interface ActivityReport {
  summary: {
    period: { start: string; end: string };
    totals: {
      activities: number;
      businessesContacted: number;
      uniqueBusinesses: number;
      meetingsBooked: number;
      referralsMade: number;
      applicationsSubmitted: number;
      followUpsScheduled: number;
    };
    byType: Record<EngagementType, number>;
    byOutcome: Record<EngagementOutcome, number>;
    trends: {
      activitiesChange: number;
      contactsChange: number;
      meetingsChange: number;
      referralsChange: number;
    };
    averages: {
      activitiesPerDay: number;
      activitiesPerUser: number;
      businessesPerUser: number;
    };
  };
  timeSeries: ActivityDataPoint[];
  userStats: UserEngagementStats[];
  topPerformers: {
    mostActive: UserEngagementStats | null;
    mostMeetings: UserEngagementStats | null;
    mostReferrals: UserEngagementStats | null;
  };
  recentActivity: EngagementActivityWithPlace[];
  categoryBreakdown: Record<string, number>;
}

// Simple mini chart component for trends
function MiniChart({
  data,
  color = "#0ea5e9",
  height = 40,
}: {
  data: number[];
  color?: string;
  height?: number;
}) {
  if (data.length === 0) return null;

  const max = Math.max(...data, 1);
  const width = 120;
  const barWidth = (width - (data.length - 1) * 2) / data.length;

  return (
    <svg width={width} height={height} className="shrink-0">
      {data.map((value, i) => {
        const barHeight = (value / max) * (height - 4);
        return (
          <rect
            key={i}
            x={i * (barWidth + 2)}
            y={height - barHeight - 2}
            width={barWidth}
            height={barHeight}
            fill={color}
            rx={2}
            opacity={0.8 + (i / data.length) * 0.2}
          />
        );
      })}
    </svg>
  );
}

// Activity bar chart
function ActivityBarChart({
  timeSeries,
}: {
  timeSeries: ActivityReport["timeSeries"];
}) {
  if (timeSeries.length === 0) {
    return (
      <div className="h-64 flex items-center justify-center text-gray-400">
        No activity data for this period
      </div>
    );
  }

  const max = Math.max(...timeSeries.map((d) => d.total), 1);
  const chartHeight = 200;
  const barWidth = Math.min(40, (100 / timeSeries.length) * 0.7);

  // Only show every nth label if too many data points
  const labelInterval =
    timeSeries.length > 14 ? Math.ceil(timeSeries.length / 7) : 1;

  return (
    <div className="relative">
      <div
        className="flex items-end justify-between gap-1 overflow-x-auto pb-8"
        style={{ height: chartHeight + 40 }}
      >
        {timeSeries.map((point, i) => {
          const barHeight = (point.total / max) * chartHeight;
          const showLabel = i % labelInterval === 0;
          const date = new Date(point.date);
          const dayLabel = date.toLocaleDateString("en-GB", {
            day: "numeric",
            month: "short",
          });

          return (
            <div
              key={point.date}
              className="flex flex-col items-center"
              style={{ minWidth: `${barWidth}%`, maxWidth: 50 }}
            >
              <div
                className="relative group cursor-pointer"
                style={{ height: chartHeight }}
              >
                <div
                  className="w-full bg-primary-500 rounded-t transition-all hover:bg-primary-600"
                  style={{
                    height: barHeight,
                    minWidth: 8,
                    position: "absolute",
                    bottom: 0,
                  }}
                />
                {/* Tooltip */}
                <div className="absolute bottom-full left-1/2 -translate-x-1/2 mb-2 opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-10">
                  <div className="bg-gray-900 text-white text-xs rounded px-2 py-1 whitespace-nowrap">
                    {point.total} activities
                    <br />
                    {date.toLocaleDateString("en-GB", {
                      weekday: "short",
                      day: "numeric",
                      month: "short",
                    })}
                  </div>
                </div>
              </div>
              {showLabel && (
                <span className="text-xs text-gray-500 mt-1 absolute bottom-0 transform -rotate-45 origin-top-left whitespace-nowrap">
                  {dayLabel}
                </span>
              )}
            </div>
          );
        })}
      </div>
      {/* Y-axis labels */}
      <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-xs text-gray-400 pointer-events-none">
        <span>{max}</span>
        <span>{Math.round(max / 2)}</span>
        <span className="mb-8">0</span>
      </div>
    </div>
  );
}

// Donut chart for type breakdown
function DonutChart({
  data,
  labels,
  icons,
  colors,
}: {
  data: Record<string, number>;
  labels: Record<string, string>;
  icons?: Record<string, string>;
  colors: string[];
}) {
  const total = Object.values(data).reduce((a, b) => a + b, 0);
  if (total === 0) {
    return (
      <div className="flex items-center justify-center h-40 text-gray-400 text-sm">
        No data
      </div>
    );
  }

  const sortedEntries = Object.entries(data)
    .filter(([_, v]) => v > 0)
    .sort((a, b) => b[1] - a[1]);

  // Create SVG donut
  const size = 140;
  const strokeWidth = 28;
  const radius = (size - strokeWidth) / 2;
  const circumference = 2 * Math.PI * radius;

  let accumulatedOffset = 0;

  return (
    <div className="flex items-center gap-4">
      <svg width={size} height={size} className="transform -rotate-90 shrink-0">
        <circle
          cx={size / 2}
          cy={size / 2}
          r={radius}
          fill="none"
          stroke="#f3f4f6"
          strokeWidth={strokeWidth}
        />
        {sortedEntries.map(([key, value], i) => {
          const percentage = value / total;
          const dashLength = circumference * percentage;
          const dashOffset = circumference * accumulatedOffset;
          accumulatedOffset += percentage;

          return (
            <circle
              key={key}
              cx={size / 2}
              cy={size / 2}
              r={radius}
              fill="none"
              stroke={colors[i % colors.length]}
              strokeWidth={strokeWidth}
              strokeDasharray={`${dashLength} ${circumference - dashLength}`}
              strokeDashoffset={-dashOffset}
              className="transition-all duration-300"
            />
          );
        })}
        <text
          x={size / 2}
          y={size / 2}
          textAnchor="middle"
          dominantBaseline="middle"
          className="fill-gray-900 font-semibold text-xl transform rotate-90"
          style={{ transformOrigin: "center" }}
        >
          {total}
        </text>
      </svg>

      <div className="flex flex-col gap-1.5 overflow-y-auto max-h-40 text-sm">
        {sortedEntries.slice(0, 6).map(([key, value], i) => (
          <div key={key} className="flex items-center gap-2">
            <div
              className="w-3 h-3 rounded-full shrink-0"
              style={{ backgroundColor: colors[i % colors.length] }}
            />
            <span className="text-gray-600 truncate">
              {icons?.[key as keyof typeof icons] || ""}{" "}
              {labels[key as keyof typeof labels] || key}
            </span>
            <span className="font-medium text-gray-900 ml-auto">{value}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

// Stat card with trend indicator
function StatCard({
  title,
  value,
  trend,
  icon,
  color = "primary",
  subtitle,
}: {
  title: string;
  value: number | string;
  trend?: number;
  icon: React.ReactNode;
  color?: "primary" | "green" | "orange" | "purple" | "blue";
  subtitle?: string;
}) {
  const colorClasses = {
    primary: "bg-primary-50 text-primary-600",
    green: "bg-green-50 text-green-600",
    orange: "bg-orange-50 text-orange-600",
    purple: "bg-purple-50 text-purple-600",
    blue: "bg-blue-50 text-blue-600",
  };

  return (
    <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
      <div className="flex items-start justify-between">
        <div className={`p-3 rounded-xl ${colorClasses[color]}`}>{icon}</div>
        {trend !== undefined && trend !== 0 && (
          <div
            className={`flex items-center gap-1 text-sm font-medium ${
              trend > 0 ? "text-green-600" : "text-red-600"
            }`}
          >
            {trend > 0 ? (
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M5 10l7-7m0 0l7 7m-7-7v18"
                />
              </svg>
            ) : (
              <svg
                className="w-4 h-4"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 14l-7 7m0 0l-7-7m7 7V3"
                />
              </svg>
            )}
            {Math.abs(trend)}%
          </div>
        )}
      </div>
      <div className="mt-4">
        <div className="text-3xl font-bold text-gray-900">{value}</div>
        <div className="text-sm text-gray-500 mt-1">{title}</div>
        {subtitle && (
          <div className="text-xs text-gray-400 mt-0.5">{subtitle}</div>
        )}
      </div>
    </div>
  );
}

// User leaderboard row
function UserLeaderboardRow({
  user,
  rank,
  highlight,
}: {
  user: UserEngagementStats;
  rank: number;
  highlight?: "activities" | "meetings" | "referrals";
}) {
  const getRankBadge = (rank: number) => {
    if (rank === 1) return "🥇";
    if (rank === 2) return "🥈";
    if (rank === 3) return "🥉";
    return `#${rank}`;
  };

  return (
    <div className="flex items-center gap-4 p-3 rounded-lg hover:bg-gray-50 transition-colors">
      <div className="w-8 text-center font-semibold text-gray-500">
        {getRankBadge(rank)}
      </div>
      <div className="flex-1 min-w-0">
        <div className="font-medium text-gray-900 truncate">
          {user.userName}
        </div>
        <div className="text-sm text-gray-500">
          {user.businessesContacted} businesses • {user.streak || 0} day streak
        </div>
      </div>
      <div className="flex items-center gap-4 text-sm">
        <div
          className={`text-center ${
            highlight === "activities"
              ? "font-bold text-primary-600"
              : "text-gray-600"
          }`}
        >
          <div className="text-lg font-semibold">{user.totalActivities}</div>
          <div className="text-xs text-gray-400">activities</div>
        </div>
        <div
          className={`text-center ${
            highlight === "meetings"
              ? "font-bold text-green-600"
              : "text-gray-600"
          }`}
        >
          <div className="text-lg font-semibold">{user.meetingsBooked}</div>
          <div className="text-xs text-gray-400">meetings</div>
        </div>
        <div
          className={`text-center ${
            highlight === "referrals"
              ? "font-bold text-purple-600"
              : "text-gray-600"
          }`}
        >
          <div className="text-lg font-semibold">{user.referralsMade}</div>
          <div className="text-xs text-gray-400">referrals</div>
        </div>
      </div>
    </div>
  );
}

// Recent activity item
function RecentActivityItem({
  activity,
}: {
  activity: EngagementActivityWithPlace;
}) {
  const icon = ENGAGEMENT_TYPE_ICONS[activity.type] || "📌";
  const label = ENGAGEMENT_TYPE_LABELS[activity.type] || activity.type;
  const outcomeLabel = activity.outcome
    ? ENGAGEMENT_OUTCOME_LABELS[activity.outcome]
    : null;

  const outcomeColors: Record<string, string> = {
    positive: "bg-green-100 text-green-700",
    neutral: "bg-gray-100 text-gray-700",
    negative: "bg-red-100 text-red-700",
    no_answer: "bg-yellow-100 text-yellow-700",
    pending: "bg-blue-100 text-blue-700",
  };

  const date = new Date(activity.createdAt);
  const timeAgo = getTimeAgo(date);

  return (
    <div className="flex items-start gap-3 p-3 rounded-lg hover:bg-gray-50 transition-colors">
      <div className="text-2xl">{icon}</div>
      <div className="flex-1 min-w-0">
        <div className="flex items-center gap-2">
          <span className="font-medium text-gray-900">{label}</span>
          {outcomeLabel && (
            <span
              className={`text-xs px-2 py-0.5 rounded-full ${
                outcomeColors[activity.outcome!]
              }`}
            >
              {outcomeLabel}
            </span>
          )}
        </div>
        <div className="text-sm text-gray-600 truncate">
          {activity.placeName}
        </div>
        <div className="text-xs text-gray-400 mt-0.5">
          {activity.createdByName || "Unknown"} • {timeAgo}
        </div>
      </div>
    </div>
  );
}

function getTimeAgo(date: Date): string {
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMs / 3600000);
  const diffDays = Math.floor(diffMs / 86400000);

  if (diffMins < 60) return `${diffMins}m ago`;
  if (diffHours < 24) return `${diffHours}h ago`;
  if (diffDays < 7) return `${diffDays}d ago`;
  return date.toLocaleDateString("en-GB", { day: "numeric", month: "short" });
}

// Main Reporting Page
export default function ReportingPage() {
  const [report, setReport] = useState<ActivityReport | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [period, setPeriod] = useState<ReportingPeriod>("30days");
  const [customDateRange, setCustomDateRange] = useState<{
    start: string;
    end: string;
  }>({
    start: new Date(Date.now() - 30 * 24 * 60 * 60 * 1000)
      .toISOString()
      .split("T")[0],
    end: new Date().toISOString().split("T")[0],
  });

  useEffect(() => {
    fetchReport();
  }, [period, customDateRange]);

  const fetchReport = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const params: any = { period };
      if (period === "custom") {
        params.startDate = customDateRange.start;
        params.endDate = customDateRange.end;
      }
      const response = await api.getActivityReport(params);
      setReport(response.data);
    } catch (err) {
      console.error("Failed to fetch activity report:", err);
      setError("Failed to load activity report. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Chart colors
  const typeColors = [
    "#0ea5e9", // primary
    "#22c55e", // green
    "#f97316", // orange
    "#a855f7", // purple
    "#ec4899", // pink
    "#14b8a6", // teal
    "#f59e0b", // amber
    "#6366f1", // indigo
    "#84cc16", // lime
    "#06b6d4", // cyan
  ];

  const outcomeColors = [
    "#22c55e", // positive - green
    "#9ca3af", // neutral - gray
    "#ef4444", // negative - red
    "#f59e0b", // no_answer - amber
    "#3b82f6", // pending - blue
  ];

  // Derived data for mini charts
  const last7DaysData = useMemo(() => {
    if (!report?.timeSeries) return [];
    return report.timeSeries.slice(-7).map((d) => d.total);
  }, [report]);

  if (isLoading && !report) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="w-12 h-12 border-4 border-primary-500 border-t-transparent rounded-full animate-spin mx-auto"></div>
          <p className="mt-4 text-gray-500">Loading activity report...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="text-red-500 text-lg mb-2">⚠️</div>
          <p className="text-gray-600">{error}</p>
          <button
            onClick={fetchReport}
            className="mt-4 px-4 py-2 bg-primary-500 text-white rounded-lg hover:bg-primary-600"
          >
            Try Again
          </button>
        </div>
      </div>
    );
  }

  if (!report) {
    return (
      <div className="flex-1 flex items-center justify-center bg-gray-50">
        <p className="text-gray-500">No report data available</p>
      </div>
    );
  }

  return (
    <div className="h-full overflow-auto bg-gray-50">
      <div className="max-w-7xl mx-auto p-4 md:p-6 pb-8">
        {/* Header */}
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">
            Activity Report
          </h1>
          <p className="text-gray-500 mt-1">
            Track your team's engagement progress and performance
          </p>
        </div>

        {/* Period Selector */}
        <div className="mb-6 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="flex flex-wrap gap-2">
            {(
              [
                "7days",
                "14days",
                "30days",
                "90days",
                "thisMonth",
                "thisQuarter",
              ] as ReportingPeriod[]
            ).map((p) => (
              <button
                key={p}
                onClick={() => setPeriod(p)}
                className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                  period === p
                    ? "bg-primary-500 text-white"
                    : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
                }`}
              >
                {REPORTING_PERIOD_LABELS[p]}
              </button>
            ))}
            <button
              onClick={() => setPeriod("custom")}
              className={`px-3 py-1.5 rounded-lg text-sm font-medium transition-colors ${
                period === "custom"
                  ? "bg-primary-500 text-white"
                  : "bg-white text-gray-600 hover:bg-gray-100 border border-gray-200"
              }`}
            >
              Custom
            </button>
          </div>

          {period === "custom" && (
            <div className="flex items-center gap-2">
              <input
                type="date"
                value={customDateRange.start}
                onChange={(e) =>
                  setCustomDateRange((prev) => ({
                    ...prev,
                    start: e.target.value,
                  }))
                }
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
              />
              <span className="text-gray-400">to</span>
              <input
                type="date"
                value={customDateRange.end}
                onChange={(e) =>
                  setCustomDateRange((prev) => ({
                    ...prev,
                    end: e.target.value,
                  }))
                }
                className="px-3 py-1.5 border border-gray-200 rounded-lg text-sm"
              />
            </div>
          )}

          {isLoading && (
            <div className="ml-auto text-sm text-gray-400 flex items-center gap-2">
              <div className="w-4 h-4 border-2 border-primary-500 border-t-transparent rounded-full animate-spin"></div>
              Updating...
            </div>
          )}
        </div>

        {/* Key Metrics */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
          <StatCard
            title="Total Activities"
            value={report.summary.totals.activities}
            trend={report.summary.trends.activitiesChange}
            icon={
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"
                />
              </svg>
            }
            color="primary"
            subtitle={`${report.summary.averages.activitiesPerDay} per day avg`}
          />
          <StatCard
            title="Businesses Contacted"
            value={report.summary.totals.businessesContacted}
            trend={report.summary.trends.contactsChange}
            icon={
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4"
                />
              </svg>
            }
            color="blue"
            subtitle={`${report.summary.totals.uniqueBusinesses} unique`}
          />
          <StatCard
            title="Meetings Booked"
            value={report.summary.totals.meetingsBooked}
            trend={report.summary.trends.meetingsChange}
            icon={
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                />
              </svg>
            }
            color="green"
          />
          <StatCard
            title="Referrals Made"
            value={report.summary.totals.referralsMade}
            trend={report.summary.trends.referralsChange}
            icon={
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M13 7l5 5m0 0l-5 5m5-5H6"
                />
              </svg>
            }
            color="purple"
          />
        </div>

        {/* Activity Timeline Chart */}
        <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 mb-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Activity Over Time
            </h2>
            <div className="flex items-center gap-2 text-sm text-gray-500">
              <span>Last 7 days:</span>
              <MiniChart data={last7DaysData} />
            </div>
          </div>
          <ActivityBarChart timeSeries={report.timeSeries} />
        </div>

        {/* Two Column Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-6">
          {/* Activity Types Breakdown */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Activity Types
            </h2>
            <DonutChart
              data={report.summary.byType}
              labels={ENGAGEMENT_TYPE_LABELS}
              icons={ENGAGEMENT_TYPE_ICONS}
              colors={typeColors}
            />
          </div>

          {/* Outcomes Breakdown */}
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Activity Outcomes
            </h2>
            <DonutChart
              data={report.summary.byOutcome}
              labels={ENGAGEMENT_OUTCOME_LABELS}
              colors={outcomeColors}
            />
          </div>
        </div>

        {/* Team Leaderboard & Recent Activity */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Team Leaderboard */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-5 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                Team Leaderboard
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Top performers this period
              </p>
            </div>
            <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
              {report.userStats.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  No team activity recorded
                </div>
              ) : (
                report.userStats.map((user, i) => (
                  <UserLeaderboardRow
                    key={user.userId}
                    user={user}
                    rank={i + 1}
                  />
                ))
              )}
            </div>
            {/* Top Performer Badges */}
            {report.topPerformers.mostActive && (
              <div className="p-4 bg-gradient-to-r from-amber-50 to-orange-50 border-t border-amber-100">
                <div className="flex items-center gap-3">
                  <div className="text-2xl">🏆</div>
                  <div>
                    <div className="text-sm font-medium text-amber-800">
                      Top Performer
                    </div>
                    <div className="text-xs text-amber-600">
                      {report.topPerformers.mostActive.userName} with{" "}
                      {report.topPerformers.mostActive.totalActivities}{" "}
                      activities
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Recent Activity */}
          <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
            <div className="p-5 border-b border-gray-100">
              <h2 className="text-lg font-semibold text-gray-900">
                Recent Activity
              </h2>
              <p className="text-sm text-gray-500 mt-1">
                Latest engagement activities
              </p>
            </div>
            <div className="divide-y divide-gray-100 max-h-96 overflow-y-auto">
              {report.recentActivity.length === 0 ? (
                <div className="p-8 text-center text-gray-400">
                  No recent activity
                </div>
              ) : (
                report.recentActivity.map((activity) => (
                  <RecentActivityItem key={activity.id} activity={activity} />
                ))
              )}
            </div>
          </div>
        </div>

        {/* Additional Stats Row */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-6">
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
            <div className="text-2xl font-bold text-gray-900">
              {report.summary.totals.applicationsSubmitted}
            </div>
            <div className="text-sm text-gray-500">Applications Submitted</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
            <div className="text-2xl font-bold text-gray-900">
              {report.summary.totals.followUpsScheduled}
            </div>
            <div className="text-sm text-gray-500">Follow-ups Scheduled</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
            <div className="text-2xl font-bold text-gray-900">
              {report.summary.averages.activitiesPerUser}
            </div>
            <div className="text-sm text-gray-500">Activities per User</div>
          </div>
          <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100 text-center">
            <div className="text-2xl font-bold text-gray-900">
              {report.summary.averages.businessesPerUser}
            </div>
            <div className="text-sm text-gray-500">Businesses per User</div>
          </div>
        </div>

        {/* Category Breakdown */}
        {Object.keys(report.categoryBreakdown).length > 0 && (
          <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100 mt-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">
              Activities by Business Category
            </h2>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3">
              {Object.entries(report.categoryBreakdown)
                .sort((a, b) => b[1] - a[1])
                .map(([category, count]) => (
                  <div
                    key={category}
                    className="bg-gray-50 rounded-lg p-3 text-center"
                  >
                    <div className="text-xl font-bold text-gray-900">
                      {count}
                    </div>
                    <div className="text-xs text-gray-500 truncate">
                      {(CATEGORY_LABELS as any)[category] || category}
                    </div>
                  </div>
                ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
